import { Helmet } from 'react-helmet-async';
import { useEffect, useRef, useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { AidaFAQ } from '../components/aida/AidaFAQ';
import { isMobileDevice, isNonWindowsOS } from '../../utility/utility';
import { SignedIn, SignedOut, SignInButton, useUser } from '@clerk/clerk-react';
import { download, setAutoDownloadFlag, sendDownloadReminderEmail } from '../../utility/api';
import { toast } from 'sonner';
import '../../styles/aida-base.css';
import '../../styles/aida-responsive.css';

const FAQHEIGHT = 700;

export const HostSteamCard = () => {
  const { user } = useUser();

  const [breakpoint, setBreakpoint] = useState<'mobile' | 'tablet' | 'desktop'>('desktop');
  const [contentScale, setContentScale] = useState(1);
  const [isContentReady, setIsContentReady] = useState(false);

  const showJoinButton = isNonWindowsOS() || isMobileDevice();

  // Handler for joining network (when not signed in)
  const handleJoinNetworkClick = useCallback(() => {
    setAutoDownloadFlag(isMobileDevice() || isNonWindowsOS());
  }, []);

  // Handler for download button (when signed in)
  const handleDownloadClick = useCallback(async () => {
    // Check if user is on mobile device or non-Windows OS
    if (isMobileDevice() || isNonWindowsOS()) {
      // On mobile or non-Windows OS, send email reminder instead of downloading
      const email = user?.primaryEmailAddress?.emailAddress;
      if (email) {
        await sendDownloadReminderEmail(email);
        toast.success('Download reminder email sent!', {
          description: 'Check your email for a link to download Playcast on your PC.',
          duration: 5000,
          style: {
            background: '#fbbf24',
            color: '#000000',
            border: 'none',
          },
        });
      } else {
        toast.error('Unable to send reminder', {
          description: 'Email address not found.',
          style: {
            background: '#fbbf24',
            color: '#000000',
            border: 'none',
          },
        });
      }
    } else {
      // On Windows desktop, proceed with normal download
      download();
    }
  }, [user]);


  // Clean scroll system: wait for content, then scroll to hash or top
  useEffect(() => {
    // Prevent browser's default scroll restoration
    window.history.scrollRestoration = 'manual';
  }, []);

  // Once content is ready, perform the final scroll
  useEffect(() => {
    if (isContentReady) {
      const hash = window.location.hash;

      if (hash) {
        // Hash present: scroll to that section
        setTimeout(() => {
          const element = document.getElementById(hash.substring(1));
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100); // Small delay to ensure rendering is complete
      } else {
        // No hash: stay at top
        window.scrollTo(0, 0);
      }
    }
  }, [isContentReady]);

  // Responsive breakpoint detection
  useEffect(() => {
    const updateBreakpoint = () => {
      const width = window.innerWidth;
      if (width <= 768) setBreakpoint('mobile');
      else if (width <= 1024) setBreakpoint('tablet');
      else setBreakpoint('desktop');
    };

    updateBreakpoint();
    window.addEventListener('resize', updateBreakpoint);
    return () => window.removeEventListener('resize', updateBreakpoint);
  }, []);

  // Update content scale for all viewports (desktop only)
  useEffect(() => {
    const updateContentScale = () => {
      const width = window.innerWidth;
      // Don't scale on mobile
      if (width <= 768) {
        setContentScale(1);
      } else {
        // Scale based on 1024px design width
        setContentScale(width / 1024);
      }
    };

    updateContentScale();
    window.addEventListener('resize', updateContentScale);
    return () => window.removeEventListener('resize', updateContentScale);
  }, []);

  // Dynamic FAQ scaling based on viewport width
  useEffect(() => {
    const updateFaqScale = () => {
      const viewportWidth = window.innerWidth;
      const designWidth = 1024;
      const mobileBreakpoint = 1024;

      const faqWrapper = document.getElementById('faq-section');

      if (faqWrapper) {
        if (viewportWidth <= mobileBreakpoint) {
          // Mobile: no scaling
          faqWrapper.style.transform = 'none';
          faqWrapper.style.height = 'auto';
          faqWrapper.style.transition = 'none';
        } else {
          // Desktop: apply proportional scaling
          const scale = viewportWidth / designWidth;
          const scaledHeight = FAQHEIGHT * scale;

          // Apply scale and set height to accommodate scaled content
          faqWrapper.style.transform = `scale(${scale})`;
          faqWrapper.style.transformOrigin = 'top center';
          faqWrapper.style.transition = 'height 0.3s ease-out';
          faqWrapper.style.height = `${scaledHeight}px`;
        }
      }
    };

    // Update scale whenever faqHeight changes or on resize
    updateFaqScale();

    // Wait for content to load before setting ready
    let contentReadySet = false;

    const handleLoad = () => {
      updateFaqScale();
      if (!contentReadySet) {
        contentReadySet = true;
        setIsContentReady(true);
      }
    };

    // Check if already loaded
    if (document.readyState === 'complete') {
      handleLoad();
    } else {
      window.addEventListener('load', handleLoad);
    }

    // Fallback: Show content after 2 seconds even if load event doesn't fire
    const fallbackTimeout = setTimeout(() => {
      if (!contentReadySet) {
        updateFaqScale();
        contentReadySet = true;
        setIsContentReady(true);
      }
    }, 2000);

    // Real-time resize handler
    window.addEventListener('resize', updateFaqScale);

    return () => {
      window.removeEventListener('load', handleLoad);
      window.removeEventListener('resize', updateFaqScale);
      clearTimeout(fallbackTimeout);
    };
  }, []);

  // Responsive styles
  const styles = {
    pageWrapper: {
      background: breakpoint === 'mobile' ? '#282828' : 'linear-gradient(180deg, #0b0b1c 0%, #18173e 10%, #18173e 70%, black 100%)',
      minHeight: '100vh',
      width: '100%',
      maxWidth: '100vw',
      margin: 0,
      padding: 0,
      overflowX: 'hidden' as const,
      fontFamily: 'Inter, -apple-system, system-ui, BlinkMacSystemFont, "Segoe UI", Ubuntu, Roboto, "Helvetica Neue", Helvetica, Arial, sans-serif',
    },
    heroSection: {
      position: 'relative' as const,
      width: '100%',
      minHeight: 'auto',
      background: 'linear-gradient(180deg, #0b0b1c 0%, #18173e 10%, #18173e 70%, black 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: breakpoint === 'mobile' ? '80px 20px 60px' : 'clamp(200px, 14vw, 240px) clamp(20px, 2vw, 40px)',
      overflow: 'visible',
    },
    heroContent: {
      position: 'relative' as const,
      width: '100%',
      maxWidth: breakpoint === 'mobile' ? '768px' : '1920px',
      display: 'flex',
      flexDirection: breakpoint === 'mobile' ? ('column' as const) : ('row' as const),
      alignItems: 'center',
      justifyContent: breakpoint === 'mobile' ? 'center' : 'space-between',
      gap: breakpoint === 'mobile' ? '20px' : 'clamp(20px, 3vw, 60px)',
      color: '#ffffff',
      padding: breakpoint === 'mobile' ? '0' : '0 clamp(20px, 4vw, 80px)',
      margin: breakpoint === 'mobile' ? '0 auto' : '0',
      boxSizing: 'border-box' as const,
    },
    heroTextContent: {
      flex: breakpoint === 'mobile' ? 'none' : '1 1 60%',
      width: breakpoint === 'mobile' ? '100%' : 'auto',
      textAlign: breakpoint === 'mobile' ? ('center' as const) : ('left' as const),
      order: breakpoint === 'mobile' ? 2 : 'initial',
      display: 'flex',
      flexDirection: 'column' as const,
      alignItems: breakpoint === 'mobile' ? 'center' : 'flex-start',
    },
    heroVisuals: {
      position: 'relative' as const,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flex: breakpoint === 'mobile' ? 'none' : '0 1 40%',
      width: breakpoint === 'mobile' ? '90%' : 'auto',
      maxWidth: breakpoint === 'mobile' ? '400px' : '500px',
      order: breakpoint === 'mobile' ? 1 : 'initial',
    },
    heroImage: {
      width: '100%',
      height: 'auto',
      filter: breakpoint === 'mobile' ? 'drop-shadow(rgba(0, 0, 0, 0.52) 25px -4px 6px)' : 'none',
    },
    heroHeading: {
      fontSize: breakpoint === 'mobile' ? 'clamp(36px, 9vw, 48px)' : 'clamp(64px, 6vw, 96px)',
      fontWeight: 900,
      fontFamily: 'Inter, sans-serif',
      marginBottom: breakpoint === 'mobile' ? '20px' : 'clamp(15px, 2vw, 30px)',
      lineHeight: '1.2',
      color: '#ffffff',
      margin: breakpoint === 'mobile' ? '0 0 20px 0' : '0 0 clamp(15px, 2vw, 30px) 0',
      whiteSpace: 'pre-line' as const,
      letterSpacing: '-0.5px',
    },
    heroSubheading: {
      fontSize: breakpoint === 'mobile' ? 'clamp(16px, 4.5vw, 20px)' : 'clamp(20px, 2vw, 32px)',
      fontWeight: 400,
      fontFamily: 'Inter, sans-serif',
      marginBottom: breakpoint === 'mobile' ? '40px' : 'clamp(30px, 3vw, 50px)',
      lineHeight: '1.6',
      color: '#ffffff',
      opacity: 0.9,
    },
    ctaButton: {
      backgroundColor: '#FBBF24',
      color: '#000000',
      fontSize: breakpoint === 'mobile' ? 'clamp(14px, 3.5vw, 16px)' : '11px',
      fontWeight: 600,
      fontFamily: 'Inter, sans-serif',
      padding: breakpoint === 'mobile' ? '14px 32px' : '8px 20px',
      borderRadius: '40px',
      border: 'none',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      width: breakpoint === 'mobile' ? '100%' : 'auto',
      maxWidth: breakpoint === 'mobile' ? '300px' : 'none',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      justifyContent: 'center',
      textDecoration: 'none',
      textAlign: 'center' as const,
      letterSpacing: '-0.08px',
    },
    ctaWrapper: {
      display: 'flex',
      flexDirection: 'column' as const,
      alignItems: 'center',
      width: breakpoint === 'mobile' ? '100%' : 'auto',
    },
    termsLink: {
      display: 'block',
      marginTop: '8px',
      fontSize: '9px',
      color: '#ffffff',
      opacity: 0.7,
      textAlign: 'center' as const,
      textDecoration: 'underline',
      fontFamily: 'Inter, sans-serif',
    },
    rewardsSection: {
      background: breakpoint === 'mobile' ? '#000000' : 'linear-gradient(180deg, #0b0b1c 0%, #18173e 10%, #18173e 70%, black 100%)',
      padding: breakpoint === 'mobile' ? '30px 20px' : '0',
      display: 'flex',
      flexDirection: 'column' as const,
      alignItems: 'center',
      justifyContent: 'flex-start',
      width: '100%',
      overflow: 'hidden',
    },
    rewardsHeading: {
      fontSize: breakpoint === 'mobile' ? 'clamp(48px, 10vw, 60px)' : '48px',
      lineHeight: breakpoint === 'mobile' ? '52px' : 2,
      fontWeight: 900,
      fontFamily: 'Inter, sans-serif',
      color: '#ffffff',
      textAlign: 'center' as const,
      marginBottom: breakpoint === 'mobile' ? '40px' : '10px',
      letterSpacing: '1px',

      fontVariationSettings: "'wght' 900",
    },
    cardsContainer: {
      display: breakpoint === 'mobile' ? 'flex' : 'grid',
      flexDirection: breakpoint === 'mobile' ? ('column' as const) : undefined,
      gridTemplateColumns: breakpoint === 'mobile' ? undefined : 'repeat(3, 1fr)',
      gap: breakpoint === 'mobile' ? '20px' : '20px',
      width: breakpoint === 'mobile' ? '100%' : '1024px',
      maxWidth: breakpoint === 'mobile' ? '768px' : '1024px',
      marginBottom: breakpoint === 'mobile' ? '25px' : '20px',
      padding: breakpoint === 'mobile' ? '0' : '0 106px',
      boxSizing: 'border-box' as const,
    },
    card: {
      background: 'linear-gradient(135deg, #4a5280 0%, #3d4466 50%, #2e3350 100%)',
      borderRadius: breakpoint === 'mobile' ? '20px' : '20px',
      padding: breakpoint === 'mobile' ? '35px 25px 15px' : '18px 24px 8px',
      display: 'flex',
      flexDirection: 'column' as const,
      alignItems: 'flex-start',
      textAlign: 'left' as const,
      overflow: 'hidden',
      height: breakpoint === 'mobile' ? 'auto' : '285px',
    },
    cardHeading: {
      fontSize: breakpoint === 'mobile' ? 'clamp(26px, 8vw, 40px)' : '22px',
      fontWeight: 800,
      fontFamily: 'Inter, sans-serif',
      color: '#ffffff',
      marginBottom: breakpoint === 'mobile' ? '20px' : '8px',
      lineHeight: '1.14',
    },
    cardText: {
      fontSize: breakpoint === 'mobile' ? 'clamp(18px, 5vw, 24px)' : '10px',
      fontWeight: 400,
      fontFamily: 'Inter, sans-serif',
      color: '#c8c8d8',
      lineHeight: '1.3',
      marginBottom: breakpoint === 'mobile' ? '30px' : '15px',
    },
    cardIcon: {
      width: '140%',
      maxWidth: 'none',
      height: 'auto',
      marginTop: '10px',
      alignSelf: 'center',
    },
    disclaimer: {
      fontSize: breakpoint === 'mobile' ? '14px' : '7px',
      fontStyle: 'italic',
      color: '#888888',
      textAlign: 'center' as const,
      width: breakpoint === 'mobile' ? '100%' : '1024px',
      maxWidth: breakpoint === 'mobile' ? '768px' : '1024px',
      marginBottom: breakpoint === 'mobile' ? '25px' : '20px',
      padding: breakpoint === 'mobile' ? '0' : '0 106px',
      fontFamily: 'Inter, sans-serif',
      boxSizing: 'border-box' as const,
    },
  };

  const downloadIcon = (
    <svg
      style={{ width: '12px', height: '12px' }}
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 512 512"
      fill="currentColor"
    >
      <path d="M288 32c0-17.7-14.3-32-32-32s-32 14.3-32 32l0 242.7-73.4-73.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3l128 128c12.5 12.5 32.8 12.5 45.3 0l128-128c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L288 274.7 288 32zM64 352c-35.3 0-64 28.7-64 64l0 32c0 35.3 28.7 64 64 64l384 0c35.3 0 64-28.7 64-64l0-32c0-35.3-28.7-64-64-64l-101.5 0-45.3 45.3c-25 25-65.5 25-90.5 0L165.5 352 64 352zm368 56a24 24 0 1 1 0 48 24 24 0 1 1 0-48z"/>
    </svg>
  );

  return (
    <>
      <Helmet>
        <title>Get a Free Steam Gift Card | Playcast</title>
        <meta
          name='description'
          content='Join Playcast and get a free Steam gift card. Become part of the Playcast Network and earn rewards while your PC is idle.'
        />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link href="https://fonts.googleapis.com/css2?family=Inter:wght@100..900&display=swap" rel="stylesheet" />
        <link href="https://fonts.googleapis.com/css?family=Roboto:400,700&display=swap" rel="stylesheet" />
      </Helmet>

      <div style={styles.pageWrapper}>
        {/* Hero Section - Mobile Only */}
        {breakpoint === 'mobile' && (
          <section style={styles.heroSection}>
            <div style={styles.heroContent}>
              {/* Text Content */}
              <div style={styles.heroTextContent}>
                <h1 style={styles.heroHeading}>
                  JOIN PLAYCAST,<br />
                  GET A <span style={{ color: '#FBBF24' }}>FREE</span><br />
                  STEAM GIFT CARD
                </h1>
                <p style={styles.heroSubheading}>
                  Become part of the Playcast Network and join the gaming revolution.
                </p>
                <div style={styles.ctaWrapper}>
                  <SignedIn>
                    <button
                      onClick={handleDownloadClick}
                      style={styles.ctaButton}
                      onMouseEnter={(e: React.MouseEvent<HTMLButtonElement>) => {
                        if (breakpoint !== 'mobile') {
                          e.currentTarget.style.backgroundColor = '#F59E0B';
                          e.currentTarget.style.transform = 'scale(1.05)';
                        }
                      }}
                      onMouseLeave={(e: React.MouseEvent<HTMLButtonElement>) => {
                        if (breakpoint !== 'mobile') {
                          e.currentTarget.style.backgroundColor = '#FBBF24';
                          e.currentTarget.style.transform = 'scale(1)';
                        }
                      }}
                    >
                      {!showJoinButton && downloadIcon}
                      {showJoinButton ? 'Join our Network' : 'Download Playcast to Get Rewards'}
                    </button>
                  </SignedIn>
                  <SignedOut>
                    <SignInButton mode="modal">
                      <button
                        onClick={handleJoinNetworkClick}
                        style={styles.ctaButton}
                        onMouseEnter={(e: React.MouseEvent<HTMLButtonElement>) => {
                          if (breakpoint !== 'mobile') {
                            e.currentTarget.style.backgroundColor = '#F59E0B';
                            e.currentTarget.style.transform = 'scale(1.05)';
                          }
                        }}
                        onMouseLeave={(e: React.MouseEvent<HTMLButtonElement>) => {
                          if (breakpoint !== 'mobile') {
                            e.currentTarget.style.backgroundColor = '#FBBF24';
                            e.currentTarget.style.transform = 'scale(1)';
                          }
                        }}
                      >
                        {!showJoinButton && downloadIcon}
                        {showJoinButton ? 'Join our Network' : 'Download Playcast to Get Rewards'}
                      </button>
                    </SignInButton>
                  </SignedOut>
                  <Link to="/host/steam-card/terms-and-conditions" style={styles.termsLink}>
                    Terms & Conditions
                  </Link>
                </div>
              </div>

              {/* Visual Assets */}
              <div style={styles.heroVisuals}>
                <img
                  src="/steam-card/images/steam-gift-card.png"
                  alt="Steam Gift Card - Get it Free"
                  style={styles.heroImage}
                />
              </div>
            </div>
          </section>
        )}

        {/* Rewards Section */}
        <section
          style={{
            ...styles.rewardsSection,
            height: breakpoint === 'mobile' ? 'auto' : `${contentScale * 515}px`,
          }}
        >
          <div style={{
            transform: breakpoint === 'mobile' ? 'none' : `scale(${contentScale})`,
            transformOrigin: 'top center',
            width: '100%',
            display: 'flex',
            flexDirection: 'column' as const,
            alignItems: 'center',
            paddingTop: breakpoint === 'mobile' ? '0' : '45px',
            paddingBottom: breakpoint === 'mobile' ? '0' : '30px',
          }}>
            <h2 style={styles.rewardsHeading}>
              {breakpoint === 'mobile' ? 'GET REWARDS OVER TIME' : 'JOIN PLAYCAST, GET REWARDS'}
            </h2>

            <div style={styles.cardsContainer}>
            {/* Card 1 */}
            <div style={styles.card}>
              <h3 style={styles.cardHeading}>INSTALL THE PLAYCAST APP</h3>
              <p style={styles.cardText}>
                Install Playcast on your PC.
              </p>
              <div style={{ flex: 1 }} />
              <img
                src="/helldivers/images/card-icon-download.png"
                alt="Install Playcast"
                style={{ ...styles.cardIcon, width: '180%', marginTop: '0px' }}
              />
            </div>

            {/* Card 2 */}
            <div style={styles.card}>
              <h3 style={styles.cardHeading}>AUTHENTICATE WITH PLAYCAST</h3>
              <p style={styles.cardText}>
                Create your account and get access to our rewards system.
              </p>
              <div style={{ flex: 1 }} />
              <img
                src="/helldivers/images/card-icon-auth.png"
                alt="Authenticate with Playcast"
                style={{ ...styles.cardIcon, width: '120%', position: 'relative' as const, top: '-20px' }}
              />
            </div>

            {/* Card 3 */}
            <div style={styles.card}>
              <h3 style={styles.cardHeading}>GET YOUR FREE STEAM CODE</h3>
              <p style={styles.cardText}>
                After 30 days as a Playcast Host, we will email you your free Steam code!
              </p>
              <div style={{ flex: 1 }} />
              <img
                src={breakpoint === 'mobile' ? '/helldivers/images/card-icon-email.png' : '/steam-card/images/card-icon-email-desktop.png'}
                alt="Get Free Steam Code"
                style={{
                  ...styles.cardIcon,
                  width: breakpoint === 'mobile' ? '150%' : '80%',
                  position: 'relative' as const,
                  top: '-20px'
                }}
              />
            </div>
          </div>

          <SignedIn>
            <button
              onClick={handleDownloadClick}
              style={styles.ctaButton}
              onMouseEnter={(e: React.MouseEvent<HTMLButtonElement>) => {
                if (breakpoint !== 'mobile') {
                  e.currentTarget.style.backgroundColor = '#F59E0B';
                  e.currentTarget.style.transform = 'scale(1.05)';
                }
              }}
              onMouseLeave={(e: React.MouseEvent<HTMLButtonElement>) => {
                if (breakpoint !== 'mobile') {
                  e.currentTarget.style.backgroundColor = '#FBBF24';
                  e.currentTarget.style.transform = 'scale(1)';
                }
              }}
            >
              {!showJoinButton && downloadIcon}
              {showJoinButton ? 'Join our Network' : 'Download Playcast to Get Rewards'}
            </button>
            <a
              href="/host/steam-card/terms-and-conditions"
              style={styles.termsLink}
              target="_blank"
              rel="noopener noreferrer"
            >
              Terms & Conditions
            </a>
          </SignedIn>
          <SignedOut>
            <SignInButton mode="modal">
              <button
                onClick={handleJoinNetworkClick}
                style={styles.ctaButton}
                onMouseEnter={(e: React.MouseEvent<HTMLButtonElement>) => {
                  if (breakpoint !== 'mobile') {
                    e.currentTarget.style.backgroundColor = '#F59E0B';
                    e.currentTarget.style.transform = 'scale(1.05)';
                  }
                }}
                onMouseLeave={(e: React.MouseEvent<HTMLButtonElement>) => {
                  if (breakpoint !== 'mobile') {
                    e.currentTarget.style.backgroundColor = '#FBBF24';
                    e.currentTarget.style.transform = 'scale(1)';
                  }
                }}
              >
                {!showJoinButton && downloadIcon}
                {showJoinButton ? 'Join our Network' : 'Download Playcast for Windows'}
              </button>
            </SignInButton>
            <Link
              to="/host/steam-card/terms-and-conditions"
              style={styles.termsLink}
            >
              Terms & Conditions
            </Link>
          </SignedOut>
          </div>
        </section>

        {/* FAQ Section */}
        <div style={{
          width: '100%',
          backgroundColor: '#000000',
          display: 'flex',
          justifyContent: 'center',
          paddingTop: breakpoint === 'mobile' ? '40px' : '60px',
          paddingBottom: breakpoint === 'mobile' ? '60px' : '80px',
          overflow: 'hidden',
        }}>
          <div
            id="faq-section"
            style={{
              width: '100%',
              maxWidth: '1024px',
            }}
          >
            <AidaFAQ />
          </div>
        </div>
      </div>
    </>
  );
};
